<template>
  <div>
    <app-template />
    <div class="uk-container uk-container-large">
      <!-- Benchmarks read from OUR stored history, so they work the same for
           whichever provider a club uses, and survive the provider's API being
           down. The live session views below still talk to the provider. -->
      <div class="gps-view-switch">
        <button
          :class="{ active: gpsView === 'sessions' }"
          @click="gpsView = 'sessions'"
        >Sessions</button>
        <button
          :class="{ active: gpsView === 'benchmarks' }"
          @click="gpsView = 'benchmarks'"
        >Benchmarks</button>
        <button
          v-if="userData.userType === 'Admin'"
          :class="{ active: gpsView === 'links' }"
          @click="gpsView = 'links'"
        >Player links</button>
      </div>

      <GpsBenchmarks v-if="gpsView === 'benchmarks'" />
      <GpsPlayerLinks v-else-if="gpsView === 'links'" />

      <template v-else>
      <!-- Catapult GPS for STJFC tenant -->
      <div v-if="isCatapultTenant" class="gps-dark-theme">
        <!-- Activity Detail View (session drilldown) -->
        <template v-if="selectedActivity">
          <div class="activity-detail-header">
            <button class="back-btn" @click="backToDashboard">
              <i class="bi bi-arrow-left"></i> Back to Dashboard
            </button>
            <div class="activity-info" v-if="activityDetail">
              <h3>{{ activityDetail.activity.name }}</h3>
              <span class="activity-type-badge" :class="activityDetail.activity.activity_type">
                {{ activityDetail.activity.activity_type }}
              </span>
              <span class="activity-date" v-if="activityDetail.activity.start_time">
                {{ formatDateForHuman(activityDetail.activity.start_time) }}
              </span>
              <span class="activity-athletes" v-if="activityDetail.team_summary">
                {{ activityDetail.team_summary.athlete_count }} athletes
              </span>
            </div>
          </div>

          <div v-if="loadingActivity" class="loading-state">
            <div class="spinner"></div>
            <p>Loading session data from OpenField...</p>
          </div>

          <div v-else-if="activityError" class="error-state">
            <i class="bi bi-exclamation-triangle"></i>
            <p>{{ activityError }}</p>
            <div class="error-actions">
              <button v-if="activitySessionExpired" class="btn-relogin" @click="reloginAndRetry" :disabled="reloggingIn">
                <i :class="reloggingIn ? 'bi bi-arrow-repeat spin' : 'bi bi-box-arrow-in-right'"></i>
                {{ reloggingIn ? 'Re-logging in...' : 'Re-login & Retry' }}
              </button>
              <button v-else class="btn-retry" @click="onViewActivity(selectedActivity)">
                <i class="bi bi-arrow-clockwise"></i> Retry
              </button>
              <button class="btn-retry" @click="backToDashboard">
                <i class="bi bi-arrow-left"></i> Back to Dashboard
              </button>
            </div>
          </div>

          <GPSSessionDashboard
            v-else-if="activityDetail"
            :sessions="activityDetailSessions"
            :sessionStats="activityDetail.athletes"
            @player-click="onPlayerClick"
          />
        </template>

        <!-- Team Dashboard (default view) -->
        <CatapultGPSTeamDashboard
          v-else
          :userData="userData"
          @view-activity="onViewActivity"
        />

        <!-- Player GPS Detail Modal -->
        <PlayerGpsDetailModal
          v-if="selectedPlayer"
          :player="selectedPlayer"
          :sessionName="selectedActivity ? selectedActivity.name : ''"
          :sessionDate="selectedActivity ? selectedActivity.start_time : ''"
          :availablePlayers="activityDetail ? activityDetail.athletes : []"
          @close="selectedPlayer = null"
        />
      </div>

      <!-- PlayerData/Eyeball GPS for PTFC and other tenants -->
      <allGPSData v-else :userData="userData" />
      </template>
    </div>
  </div>
</template>

<script lang="js">
import AppTemplate from "../layouts/App";
import { Users } from "@/services/user-service.js";
import extendSeasonEndDateToFullYearMixin from "@/mixin/extendSeasonEndDateToFullYear.js";
import { mapGetters } from "vuex";
import { CHECK_PROFILE_HAS_DATA } from "@/store/actions/user.js";
import allGPSData from "@/components/GpsReports/index.vue";
import CatapultGPSTeamDashboard from "@/components/CatapultGps/GPSTeamDashboard.vue";
import GpsBenchmarks from "@/components/GpsBenchmarks/index.vue";
import GpsPlayerLinks from "@/components/GpsPlayerLinks/index.vue";
import GPSSessionDashboard from "@/components/CatapultGps/GPSSessionDashboard.vue";
import PlayerGpsDetailModal from "@/components/CatapultGps/PlayerGpsDetailModal.vue";
import { isFeatureEnabled } from "@/config/tenant";
import { catapultService } from "@/services/catapult-service";

export default {
  name: "AllGPSReports",
  components: { AppTemplate, allGPSData, CatapultGPSTeamDashboard, GPSSessionDashboard, PlayerGpsDetailModal, GpsBenchmarks, GpsPlayerLinks },
  mixins: [extendSeasonEndDateToFullYearMixin],
  data() {
    return {
      gpsView: "sessions",
      userData: {
        name: "",
        userType: "",
      },
      clubTeams: [],
      // Activity detail state
      selectedActivity: null,
      activityDetail: null,
      loadingActivity: false,
      selectedPlayer: null,
      activityError: null,
      activitySessionExpired: false,
      reloggingIn: false,
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    isCatapultTenant() {
      return isFeatureEnabled("catapultIntegration");
    },
    activityDetailSessions() {
      if (!this.activityDetail) return [];
      return [this.activityDetail.activity];
    },
  },
  created() {
    this.$store.dispatch(CHECK_PROFILE_HAS_DATA).then((response) => {
      if (response === true) {
        this.setUser();
        this.getUserById(this.userData.id);
      }
    });
  },
  methods: {
    async onViewActivity(activity) {
      this.selectedActivity = activity;
      this.activityDetail = null;
      this.loadingActivity = true;
      this.selectedPlayer = null;
      this.activityError = null;
      this.activitySessionExpired = false;

      try {
        const clubId = this.$store.getters.clubId;
        const response = await catapultService.getActivityDetail(clubId, activity.id);
        if (response.data && response.data.success) {
          this.activityDetail = response.data;
        } else {
          const msg = response.data?.message || "Failed to load activity detail";
          this.activityError = msg;
          this.activitySessionExpired = !!response.data?.session_expired;
          console.error("Failed to load activity detail:", msg);
        }
      } catch (error) {
        this.activityError = "Connection error — please try again.";
        console.error("Error loading activity detail:", error);
      } finally {
        this.loadingActivity = false;
      }
    },
    async reloginAndRetry() {
      this.reloggingIn = true;
      this.activityError = "Re-logging in to OpenField...";
      try {
        const clubId = this.$store.getters.clubId;
        const loginResult = await catapultService.scraperLogin(clubId, {});
        if (!loginResult.data.success) {
          // A refusal (blocked) is not a failed sign-in — the server declined
          // to try, because repeated attempts with a stale password are what
          // lock the Catapult account. Say so instead of prompting a retry.
          this.activityError = loginResult.data.blocked
            ? loginResult.data.message + (loginResult.data.hint ? " " + loginResult.data.hint : "")
            : (loginResult.data.message || "Re-login failed. Please use Settings to log in manually.");
          return;
        }
        // Login succeeded — retry the activity load
        this.activityError = null;
        this.activitySessionExpired = false;
        await this.onViewActivity(this.selectedActivity);
      } catch (error) {
        this.activityError = "Re-login failed — please use Settings to log in manually.";
        console.error("Re-login error:", error);
      } finally {
        this.reloggingIn = false;
      }
    },
    backToDashboard() {
      this.selectedActivity = null;
      this.activityDetail = null;
      this.selectedPlayer = null;
      this.activityError = null;
      this.activitySessionExpired = false;
    },
    onPlayerClick(player) {
      this.selectedPlayer = { ...player };
    },
    getPlayerData(playerId) {
      return this.currentTeam.find((player) => player.id === playerId);
    },
    isStringNumberBetween0And5(str) {
      const num = Number(str);
      return !isNaN(num) && num >= 0 && num <= 5;
    },
    checkForInjuredPlayers(attendeeList) {
      return attendeeList;
    },
    async getUserByTeamId() {
      this.currentTeam = [];
      const userData = {
        teamId: this.selectedTeam.id,
        action: "getUserByTeamId",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.currentTeam = responce.data;
      }
    },
    setUser() {
      if (this.getProfile !== null) {
        this.userData = this.getProfile;
      }
    },
    async getUserById(clientId) {
      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.userData = responce.data[0];
      }
    },
    getDayAndDate(date) {
      const d = new Date(date);
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const dayName = days[d.getDay()];
      const monthName = months[d.getMonth()];
      const day = d.getDate();
      const year = d.getFullYear();
      function ordinal(n) {
        const s = ["th", "st", "nd", "rd"],
          v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      }
      return `${dayName}, ${monthName} ${ordinal(day)} ${year}`;
    },
    formatISOStringDateAndTime(timeDateString) {
      const d = new Date(timeDateString);
      let hours = d.getUTCHours();
      const minutes = d.getUTCMinutes();
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const mins = minutes < 10 ? `0${minutes}` : minutes;
      return `${hours}:${mins} ${ampm}`;
    },
    getGameTypeBadgeClass(gameType) {
      const type = gameType.toLowerCase();
      if (type.includes("friendly")) return "friendly";
      if (type.includes("league")) return "league";
      if (type.includes("cup")) return "cup";
      if (type.includes("tournament")) return "tournament";
      return "default";
    },
    getGameStatusClass(event) {
      const matchDate = new Date(event.matchDate);
      const today = new Date();

      if (event.result_added === "1") {
        return "completed";
      } else if (matchDate < today) {
        return "in-progress";
      } else {
        return "upcoming";
      }
    },
    formatDateForHuman(date) {
      if (!date) return "Invalid date";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "Invalid date";
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const monthName = months[d.getMonth()];
      const day = d.getDate();
      const year = d.getFullYear();
      function ordinal(n) {
        const s = ["th", "st", "nd", "rd"],
          v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      }
      return `${monthName} ${ordinal(day)} ${year}`;
    },
  },
};
</script>
<style scoped>
.gps-view-switch { display: flex; gap: .5rem; padding: 1rem 0 .25rem; }
.gps-view-switch button {
  border: 1px solid #ced4da; background: #fff; color: #495057;
  padding: .4rem .9rem; border-radius: 6px; font-size: .85rem; cursor: pointer;
}
.gps-view-switch button.active { background: #212529; border-color: #212529; color: #fff; font-weight: 600; }
</style>

<style scoped lang="scss">
/* ========================================
   Dark Theme Variables & Container
   ======================================== */
.gps-dark-theme {
  /* Core colors */
  --color-bg: #1a1d2e;
  --color-surface: #2d3142;
  --color-surface-alt: #252838;
  --color-surface-raised: #363a4f;

  /* Borders */
  --color-border: #404258;
  --color-border-light: #353849;

  /* Text */
  --color-text: #e8eaed;
  --color-text-secondary: #adb5bd;
  --color-text-muted: #6c757d;

  /* Accents */
  --color-primary: #667eea;
  --color-primary-light: #7c8ef5;
  --color-primary-bg: rgba(102, 126, 234, 0.15);
  --color-primary-gradient: linear-gradient(135deg, #667eea, #764ba2);
  --color-success: #28a745;
  --color-warning: #ffc107;
  --color-danger: #dc3545;
  --color-info: #17a2b8;

  /* Layout */
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-full: 999px;
  --space-sm: 8px;
  --space-md: 16px;
  --space-lg: 24px;
  --space-xl: 32px;
  --space-2xl: 48px;
  --shadow-lg: 0 8px 32px rgba(0, 0, 0, 0.4);
  --transition-fast: 150ms ease;

  /* Legacy variable aliases for GPSTeamDashboard */
  --primary: #667eea;
  --bg-secondary: #2d3142;
  --bg-tertiary: #252838;
  --border-color: #404258;
  --text-muted: #6c757d;

  background: var(--color-bg);
  color: var(--color-text);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);
  min-height: 400px;
}

/* ========================================
   Activity Detail Header
   ======================================== */
.activity-detail-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 0 16px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;

  &:hover {
    background: var(--color-surface-raised);
    border-color: var(--color-primary);
    color: var(--color-text);
  }
}

.activity-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .activity-type-badge {
    display: inline-block;
    padding: 3px 12px;
    border-radius: var(--radius-full);
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &.match {
      background: rgba(40, 167, 69, 0.2);
      color: #5dd67a;
    }
    &.training {
      background: rgba(102, 126, 234, 0.2);
      color: #7c8ef5;
    }
  }

  .activity-date,
  .activity-athletes {
    font-size: 0.85rem;
    color: var(--color-text-muted);
  }

  .activity-athletes::before {
    content: '|';
    margin-right: 12px;
    opacity: 0.3;
  }
}

/* ========================================
   Loading State
   ======================================== */
.loading-state {
  text-align: center;
  padding: 80px 0;
  color: var(--color-text-secondary);

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--color-border);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 16px;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ========================================
   Error State
   ======================================== */
.error-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--color-text-secondary);

  .bi-exclamation-triangle {
    font-size: 2.5rem;
    color: var(--color-warning, #ffc107);
    display: block;
    margin-bottom: 12px;
  }

  p {
    margin: 0 0 20px;
    font-size: 0.95rem;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
}

.error-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.btn-retry {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--color-surface-raised);
    border-color: var(--color-primary);
    color: var(--color-text);
  }
}

.btn-relogin {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  background: var(--color-primary, #667eea);
  border: none;
  border-radius: var(--radius-md);
  color: #fff;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: #5a72d4;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }
}
</style>
